import { CLIENT_REGISTRATION_TYPES } from 'constants/clientRegistration';
import { CARE_TYPE_INPATIENT, CARE_TYPE_OUTPATIENT } from 'constants/treatment';

export const CONSULTATION_CATEGORY_NAME = 'Консультации';

export function getServiceDoctorIds(service) {
  const raw = service?.doctorIds ?? service?.doctor_ids ?? [];
  return Array.isArray(raw) ? raw.map((id) => Number(id)).filter((id) => id > 0) : [];
}

/** Услуга без привязки доступна всем врачам (обратная совместимость). */
export function serviceAvailableForDoctor(service, doctorId) {
  if (!doctorId) return true;
  const ids = getServiceDoctorIds(service);
  if (!ids.length) return true;
  return ids.some((id) => String(id) === String(doctorId));
}

export function filterServicesForDoctor(services, doctorId) {
  const list = Array.isArray(services) ? services : [];
  if (!doctorId) return list;
  return list.filter((service) => serviceAvailableForDoctor(service, doctorId));
}

export function getServiceCategoryName(service) {
  return String(
    service?.categoryName ?? service?.category_name ?? service?.category?.name ?? ''
  ).trim();
}

export function isConsultationService(service) {
  const category = getServiceCategoryName(service);
  if (category === CONSULTATION_CATEGORY_NAME || /консультац/i.test(category)) {
    return true;
  }
  const name = String(service?.name || '').trim();
  return /^консультация\b/i.test(name);
}

export const ENDOSCOPY_CATEGORY_NAME = 'Эндоскопия';

/** Амбулаторные ЭГДС / колоноскопия — в расписании как основная услуга, в отчёте не консультация. */
export function isEndoscopyService(service) {
  const category = getServiceCategoryName(service);
  if (category === ENDOSCOPY_CATEGORY_NAME || /^эндоскоп/i.test(category)) {
    return true;
  }
  const name = String(service?.name || '').trim().toLowerCase();
  if (/эгдс|egds|колоноскоп|colonoscop/.test(name)) return true;
  if (/полипэктоми|polypectom/.test(name)) return true;
  return name.includes('полип') && /удал|резекц|снят|толст/.test(name);
}

export function isSpirometryService(service) {
  const name = String(service?.name || '').trim().toLowerCase();
  return name !== '' && name.includes('спирометр');
}

/** Гипс / вправление — основные приёмы травматолога, не доп. услуга. */
export function isAmbulatoryTraumaProcedureService(service) {
  const name = String(service?.name || '').trim().toLowerCase();
  if (name === '') return false;
  return /вправлен|гипсов|гипс/.test(name);
}

/** Основная запись в расписании: консультация, ЭГДС, спирометрия, гипс/вправление. Не плазма/УВТ. */
export function isSchedulePrimaryAmbulatoryService(service) {
  if (!isStandardService(service)) return false;
  return (
    isConsultationService(service) ||
    isEndoscopyService(service) ||
    isSpirometryService(service) ||
    isAmbulatoryTraumaProcedureService(service)
  );
}

export function getMasterServiceIds(master) {
  const raw = master?.serviceIds ?? master?.service_ids ?? [];
  return Array.isArray(raw) ? raw.map((id) => Number(id)).filter((id) => id > 0) : [];
}

function serviceLinkedToDoctor(service, doctorId) {
  if (!doctorId) return false;
  return getServiceDoctorIds(service).some((id) => String(id) === String(doctorId));
}

/** Пустой serviceIds у врача — все услуги каталога (как в DoctorServicesSelect). */
export function serviceAttachedToMaster(service, master, doctorId = null) {
  const ids = getMasterServiceIds(master);
  if (!ids.length) return true;
  if (ids.some((id) => String(id) === String(service?.id))) return true;
  // Каталог при открытии записи свежее, чем колонка врача в уже открытом расписании.
  return isEndoscopyService(service) && serviceLinkedToDoctor(service, doctorId ?? master?.id);
}

export function serviceMatchesVisitType(service, visitType) {
  const care = service?.careType ?? service?.care_type ?? CARE_TYPE_OUTPATIENT;
  const inpatientVisit =
    visitType === CLIENT_REGISTRATION_TYPES.INPATIENT || visitType === CARE_TYPE_INPATIENT;
  return inpatientVisit ? care === CARE_TYPE_INPATIENT : care !== CARE_TYPE_INPATIENT;
}

export function isStandardService(service) {
  return (service?.serviceKind ?? service?.service_kind ?? 'standard') === 'standard';
}

/** Услуги, отмеченные в профиле врача. */
export function getMasterAttachedServices(services, master, doctorId = null) {
  const ids = getMasterServiceIds(master);
  const list = Array.isArray(services) ? services : [];
  if (!ids.length) return [];
  return list.filter((service) => serviceAttachedToMaster(service, master, doctorId ?? master?.id));
}

/** Подсказка, почему в расписании пустой список услуг. */
export function describeScheduleServicesGap(services, options = {}) {
  const { visitType, master } = options;
  if (!master) return 'Сначала выберите врача.';
  const doctorId = options.doctorId ?? master?.id;

  const masterIds = getMasterServiceIds(master);
  if (!masterIds.length) {
    return 'У врача не отмечены услуги. Откройте Врачи → редактирование → «Услуги врача» и отметьте консультацию.';
  }

  const attached = getMasterAttachedServices(services, master, doctorId);
  if (!attached.length) {
    return 'Отмеченные услуги не найдены в каталоге. Сохраните профиль врача ещё раз.';
  }

  const inpatientVisit =
    visitType === CLIENT_REGISTRATION_TYPES.INPATIENT || visitType === CARE_TYPE_INPATIENT;
  const visitLabel = inpatientVisit ? 'стационарной' : 'амбулаторной';
  const neededCareLabel = inpatientVisit ? 'Стационарное' : 'Амбулаторное';

  const matchingCare = attached.filter((service) => serviceMatchesVisitType(service, visitType));
  if (!matchingCare.length) {
    const sample = attached
      .slice(0, 3)
      .map((service) => service.name)
      .join(', ');
    return `Для ${visitLabel} записи у врача отмечены только услуги другого типа (${sample}). В профиле врача отметьте услугу с формой лечения «${neededCareLabel}».`;
  }

  const matchingPrimary = inpatientVisit
    ? matchingCare.filter(isStandardService)
    : matchingCare.filter(isSchedulePrimaryAmbulatoryService);

  if (!matchingPrimary.length) {
    const sample = matchingCare
      .slice(0, 3)
      .map((service) => service.name)
      .join(', ');
    if (inpatientVisit) {
      return `Отмечены доп. услуги (${sample}) — они не подходят для записи в расписании. В профиле врача отметьте основную услугу из раздела «Основные услуги».`;
    }
    return `У врача отмечены доп. услуги (${sample}), но нет основной амбулаторной услуги. В профиле отметьте консультацию, ЭГДС, спирометрию или гипс / вправление.`;
  }

  return null;
}

export function filterPrimaryBookingServices(
  services,
  visitType,
  currentServiceId = null,
  doctorId = null,
  masterServiceIds = null
) {
  const list = Array.isArray(services) ? services : [];
  const attachedIds = Array.isArray(masterServiceIds)
    ? new Set(masterServiceIds.map((id) => String(id)))
    : null;

  return list.filter((s) => {
    if (!serviceMatchesVisitType(s, visitType)) return false;
    if (doctorId && !serviceAvailableForDoctor(s, doctorId)) return false;
    if (isStandardService(s)) return true;
    if (attachedIds?.has(String(s.id))) return true;
    if (currentServiceId && String(s.id) === String(currentServiceId)) return true;
    return false;
  });
}

function getAttachedServicesForDoctor(services, options = {}) {
  const { visitType, doctorId, master, currentServiceId } = options;
  // Если у врача в профиле отмечены услуги — это главный список для расписания.
  // Не дублируем фильтр service.doctorIds: он может не совпадать с галочками в карточке врача.
  const masterServiceIds = getMasterServiceIds(master);
  const restrictByServiceDoctors = masterServiceIds.length === 0;
  const doctorFilterId = restrictByServiceDoctors ? doctorId : null;

  return filterPrimaryBookingServices(
    services,
    visitType,
    currentServiceId,
    doctorFilterId,
    masterServiceIds
  )
    .filter((service) => serviceAttachedToMaster(service, master, doctorId))
    .filter(isStandardService);
}

/** Список услуг для поля «Услуга» в расписании. */
export function resolveScheduleServicesForDoctor(services, options = {}) {
  return getAttachedServicesForDoctor(services, options);
}

export function isServiceValidForSchedulePicker(serviceId, services, options = {}) {
  if (!serviceId) return false;
  return resolveScheduleServicesForDoctor(services, {
    ...options,
    currentServiceId: serviceId,
  }).some((service) => String(service.id) === String(serviceId));
}

/**
 * При смене врача в существующей записи — подобрать консультацию нового врача
 * (не оставлять услугу прежней специальности, даже если она «подходит» по фильтру).
 */
export function pickServiceWhenDoctorChanges(services, options = {}, previousServiceId = '') {
  const pickerList = resolveScheduleServicesForDoctor(services, {
    ...options,
    currentServiceId: null,
  });

  if (!pickerList.length) {
    return '';
  }

  const consultations = pickerList.filter(isSchedulePrimaryAmbulatoryService);
  const candidates = consultations.length ? consultations : pickerList;

  if (previousServiceId) {
    const different = candidates.find((s) => String(s.id) !== String(previousServiceId));
    if (different) {
      return String(different.id);
    }
  }

  return String(candidates[0].id);
}

/** ID услуги для автоподстановки при выборе врача. Пустая строка — выбор вручную. */
export function resolveDefaultScheduleServiceId(services, options = {}) {
  const { currentServiceId } = options;
  const pickerList = resolveScheduleServicesForDoctor(services, options);

  if (
    currentServiceId &&
    pickerList.some((service) => String(service.id) === String(currentServiceId))
  ) {
    return String(currentServiceId);
  }

  const attached = getAttachedServicesForDoctor(services, options);

  if (attached.length === 1) {
    return String(attached[0].id);
  }

  return '';
}
